import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { useDialogs, useUser, useUserPermissions } from '@paintscout/ui-business'
import { useInternalStaff } from 'src/hooks/useInternalStaff'
import PaymentFailureDialog from './PaymentFailureDialog'

/** The notice appears once a day for the first three days of an episode. */
const NOTICE_DAYS = 3
const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Per-user, per-day "already nagged" stamp.
 *
 * localStorage rather than `user_metadata`: writing the stamp through `updateUser` would fire a
 * HubSpot sync and a Chargebee sync on every dialog dismissal, which is a lot of downstream work
 * for a nag counter. The cost is that a second device shows the dialog once more that day. For a
 * payment reminder that is an acceptable trade.
 *
 * Scoped by user id, and whitelisted in `clearUserData` so logging out does not reset the nag —
 * without the whitelist, signing out and back in on the same day showed the notice again.
 *
 * The scoping is what makes the whitelist safe. A bare key surviving logout would be adopted by
 * the next person to sign in on the same browser, so one billing admin's dismissal would silence
 * the notice for their colleague. That is exactly the shape of the #22045 `dashboardOptions` bug,
 * and the reason the whitelist entry is a partial match on the prefix.
 */
const STORAGE_KEY_PREFIX = 'paymentFailureNoticeShown:'

export function getNoticeStorageKey(userId?: string): string {
  return `${STORAGE_KEY_PREFIX}${userId ?? 'unknown'}`
}

export interface PaymentFailureValue {
  /** Opens the dialog straight on the card form — what the banner's Pay Now does. */
  openPaymentForm: () => void
}

const PaymentFailureContext = createContext<PaymentFailureValue>({ openPaymentForm: () => {} })

export function usePaymentFailure(): PaymentFailureValue {
  return useContext(PaymentFailureContext)
}

/** Which day of the episode we are on. Day 1 is the day the payment first failed. */
export function getEpisodeDay(firstPaymentFailedAt: number, now: number): number {
  return Math.floor((now - firstPaymentFailedAt) / DAY_MS) + 1
}

function readStamp(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    // Private mode, or storage disabled. Treat as "not shown" — an extra nag beats a silent one.
    return null
  }
}

function writeStamp(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Same reasoning. The dialog re-appears next reload, which is survivable.
  }
}

function clearStamp(key: string) {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // Nothing to do — a stale stamp only suppresses a nag the user no longer needs.
  }
}

/**
 * Mounts the failed-payment notice app-wide (ENG-1532).
 *
 * App-wide rather than on the dashboard: a user who lands straight on /quotes and never opens the
 * dashboard still has to be told their payment failed.
 *
 * Who sees it: a past-due account, on days 1-3 of the episode, once per day, for a user who can
 * actually fix it. Everyone else gets the header banner only — showing a card form to a user with
 * no billing permission just wastes their time, and showing one to a superadmin or a masquerading
 * session risks charging a card against the wrong account.
 */
export function PaymentFailureProvider({ children }: { children: React.ReactNode }) {
  const { dunningStatus, dunning, user } = useUser()
  const isInternalStaff = useInternalStaff()
  const { permissionToUpdateBilling } = useUserPermissions()
  const { openDialog, dismissDialog, stack: dialogStack } = useDialogs()

  const isPastDue = dunningStatus === 'past_due'
  const canPay = Boolean(permissionToUpdateBilling) && !isInternalStaff

  // Goes onto the shared dialog stack rather than rendering inline, so this dialog queues and
  // stacks with every other one in the app instead of appearing over the top of them.
  const show = useCallback(
    (startOnCardForm: boolean) => {
      openDialog(PaymentFailureDialog, { startOnCardForm, onClose: dismissDialog })
    },
    [openDialog, dismissDialog]
  )

  const openPaymentForm = useCallback(() => show(true), [show])

  useEffect(() => {
    if (!isPastDue || !canPay) {
      // The episode is over (or this user was never the audience). Drop the stamp so the next
      // episode starts its own three days from scratch.
      if (!isPastDue) clearStamp(getNoticeStorageKey(user?.user_id))
      return
    }

    // Never interrupt whatever the user already has open — the banner is always there, so the
    // notice can wait for a clear moment. Matches OnboardUser's guard.
    if (dialogStack.length > 0) return

    const firstPaymentFailedAt = dunning?.firstPaymentFailedAt
    // The Auth0 token fallback carries `status` with no dates. Without an anchor there is no day
    // to count, so show the notice — the banner alone would under-warn someone about to lose
    // their account.
    const day = firstPaymentFailedAt ? getEpisodeDay(firstPaymentFailedAt, Date.now()) : 1
    if (day > NOTICE_DAYS) return

    const storageKey = getNoticeStorageKey(user?.user_id)
    const today = new Date().toDateString()
    if (readStamp(storageKey) === today) return

    writeStamp(storageKey, today)
    show(false)
    // `dialogStack` is deliberately absent: including it would re-run this every time any dialog
    // anywhere in the app opened or closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPastDue, canPay, user?.user_id, dunning?.firstPaymentFailedAt])

  const value = useMemo(() => ({ openPaymentForm }), [openPaymentForm])

  return <PaymentFailureContext.Provider value={value}>{children}</PaymentFailureContext.Provider>
}

export default PaymentFailureProvider
